"use client";

import { useId, useRef, useState } from "react";
import { Plus, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobileLandscape } from "@/hooks/use-mobile-landscape";
import { SECTION_CONTAINER, SECTION_HEADING, SECTION_PADDING } from "@/lib/home-section-layout";

interface FAQCategory {
  id: string;
  name: string;
  faqs: FAQItem[];
}

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  className?: string;
  title?: string;
  categories?: FAQCategory[];
}

export function FAQSection({
  className,
  title,
  categories = [],
}: FAQSectionProps) {
  const isMobileLandscape = useIsMobileLandscape();
  const baseId = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Seeded from the data rather than a hardcoded id. The previous default was
  // "support", which matches no category the page supplies, so the panel fell
  // back to categories[0] while the rail highlighted nothing.
  const [activeCategory, setActiveCategory] = useState(
    () => categories[0]?.id ?? ""
  );
  const [openFAQs, setOpenFAQs] = useState<Record<string, boolean>>({});

  const toggleFAQ = (categoryId: string, faqIndex: number) => {
    const key = `${categoryId}-${faqIndex}`;
    setOpenFAQs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const activeIndex = Math.max(
    0,
    categories.findIndex((c) => c.id === activeCategory)
  );
  const currentCategory = categories[activeIndex];

  const headingId = `${baseId}-heading`;
  const tabId = (id: string) => `${baseId}-tab-${id}`;
  const panelId = (id: string) => `${baseId}-panel-${id}`;

  /**
   * Roving-tabindex keyboard support for the category rail. The rail renders
   * as a row on mobile and a column from md up, so both axes move selection
   * rather than binding to one orientation the user may not be looking at.
   */
  const onTabKeyDown = (event: React.KeyboardEvent, index: number) => {
    const lastIndex = categories.length - 1;
    let next: number | null = null;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = index === lastIndex ? 0 : index + 1;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = index === 0 ? lastIndex : index - 1;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    setActiveCategory(categories[next].id);
    tabRefs.current[next]?.focus();
  };

  if (!currentCategory) return null;

  return (
    <section id="faq" data-surface="base" className={cn(SECTION_PADDING, "font-outfit bg-site", className)}>
      <div className={cn(SECTION_CONTAINER, isMobileLandscape && "!mx-6")}>
        <h2
          id={headingId}
          className={cn(SECTION_HEADING, "mb-8 md:mb-10")}
        >
          {title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 ">
          <div
            role="tablist"
            aria-labelledby={headingId}
            className="md:col-span-1 gap-4 flex md:flex-col justify-between md:justify-start "
          >
            {categories.map((category, index) => {
              const isActive = category.id === activeCategory;
              return (
                <button
                  key={category.id}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  type="button"
                  role="tab"
                  id={tabId(category.id)}
                  aria-selected={isActive}
                  aria-controls={panelId(category.id)}
                  // Only the selected tab is a tab stop; arrows move between
                  // them. This is what makes a tablist one stop, not N.
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveCategory(category.id)}
                  onKeyDown={(event) => onTabKeyDown(event, index)}
                  className={cn(
                    "text-left md:py-3 rounded-sm transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black",
                    isActive
                      ? "border-b-[3px] pb-3  md:border-b-0 md:border-l-4 border-yellow-500 md:pl-4 font-medium"
                      : "md:pl-5 text-gray-400 hover:text-white"
                  )}
                >
                  {category.name}
                </button>
              );
            })}
          </div>

          {/* Every category gets a panel, not just the selected one. A tab's
              `aria-controls` has to resolve to an element that exists, so
              rendering only the active panel would leave the other tabs
              pointing at nothing. Hiding the inactive panels also keeps their
              answers in the server-rendered HTML, where a crawler can read
              them. */}
          {categories.map((category) => (
            <div
              key={category.id}
              role="tabpanel"
              id={panelId(category.id)}
              aria-labelledby={tabId(category.id)}
              hidden={category.id !== currentCategory.id}
              className="md:col-span-3 "
            >
              {category.faqs.map((faq, index) => {
                const isOpen = Boolean(openFAQs[`${category.id}-${index}`]);
                const questionId = `${baseId}-q-${category.id}-${index}`;
                const answerId = `${baseId}-a-${category.id}-${index}`;

                return (
                  <div
                    key={index}
                    className={cn(
                      "border-b border-zinc-800",
                      index === 0 && "border-t"
                    )}
                  >
                    {/* The heading carries the level; the button carries the
                        interaction. Nesting them this way keeps the document
                        outline intact while making the row operable. */}
                    <h3>
                      <button
                        type="button"
                        id={questionId}
                        aria-expanded={isOpen}
                        aria-controls={answerId}
                        onClick={() => toggleFAQ(category.id, index)}
                        className={cn(
                          "flex w-full justify-between items-center gap-4 py-6 text-left",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                        )}
                      >
                        <span className="text-lg font-medium">
                          {faq.question}
                        </span>
                        <span
                          aria-hidden="true"
                          className="bg-zinc-800 rounded-full p-2 shrink-0"
                        >
                          {isOpen ? (
                            <Minus className="h-5 w-5 text-yellow-500" />
                          ) : (
                            <Plus className="h-5 w-5 text-yellow-500" />
                          )}
                        </span>
                      </button>
                    </h3>
                    <div
                      id={answerId}
                      role="region"
                      aria-labelledby={questionId}
                      hidden={!isOpen}
                      className="pb-6 text-gray-300"
                    >
                      {faq.answer}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
